import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, notFound } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { myReluxesQuery, projectsQuery, reluxQuery, settingsQuery } from '@/api/queries'
import { flag, isFieldUser } from '@/auth/me'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/form/select'
import { DesignView } from '@/features/relux/view/design-view'
import { RoiTool } from '@/features/tools/roi-tool'

type CaseStudy = { title: string; text: string; image?: string }

/**
 * Presentation mode: no queue, notes, notifications or navigation, just Mesmerize, case studies,
 * the project's design and its savings. The app shell hides its chrome on this route.
 */
export const Route = createFileRoute('/_app/present')({
  validateSearch: (s: Record<string, unknown>): { relux?: string } => ({
    relux: typeof s.relux === 'string' ? s.relux : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!flag(context.me, 'tool_presentation_mode')) throw notFound()
  },
  component: Present,
})

function Present() {
  const { relux } = Route.useSearch()
  const { me } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const settings = useQuery(settingsQuery)
  const mine = useQuery({ ...myReluxesQuery, enabled: isFieldUser(me) })
  const all = useQuery({ ...projectsQuery(''), enabled: !isFieldUser(me) })
  const detail = useQuery({ ...reluxQuery(relux ?? ''), enabled: !!relux })
  const cases = (settings.data?.case_studies as CaseStudy[] | undefined) ?? []
  const options = isFieldUser(me)
    ? (mine.data ?? [])
        .filter((r) => r.status === 'completed' || r.status === 'closed')
        .map((r) => ({ value: r.id, label: r.project_name }))
    : (all.data ?? [])
        .filter((p) => p.latest?.completed_at)
        .map((p) => ({ value: p.latest!.id, label: p.name }))

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Project to present"
          value={relux ?? ''}
          onChange={(e) => void navigate({ search: { relux: e.target.value || undefined } })}
          options={options}
          placeholder="Choose a project (optional)"
          className="min-w-64 flex-1"
        />
        <Button asChild variant="outline">
          <Link to="/tools">
            <X aria-hidden /> Exit presentation
          </Link>
        </Button>
      </div>

      <section className="relative overflow-hidden rounded-card bg-ink px-6 py-14 text-center text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 left-1/2 h-80 w-[40rem] -translate-x-1/2 rounded-full bg-brand/30 blur-3xl"
        />
        <Logo className="relative mx-auto h-48" />
        <p className="relative mt-6 font-display text-3xl tracking-wider uppercase">Ask for Mez</p>
        <p className="relative mx-auto mt-3 max-w-2xl text-lg text-white/80">
          South African LED lighting for commercial, industrial, residential and outdoor projects.
          Full specifications, datasheets and designs by our in-house lighting designer.
        </p>
      </section>

      {cases.length > 0 && (
        <section className="grid gap-4 md:grid-cols-3">
          {cases.map((c) => (
            <article
              key={c.title}
              className="overflow-hidden rounded-card border border-border bg-surface"
            >
              {c.image && (
                <img
                  src={c.image}
                  alt=""
                  className="aspect-video w-full object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-4">
                <h2 className="text-xl uppercase">{c.title}</h2>
                <p className="text-fg-muted">{c.text}</p>
              </div>
            </article>
          ))}
        </section>
      )}

      {detail.data && (
        <>
          <DesignView detail={detail.data} />
          {detail.data.spaces.some((s) => s.existing_lighting.length > 0) && (
            <section className="flex flex-col gap-4">
              <h2 className="text-2xl uppercase">Energy savings</h2>
              <RoiTool reluxId={relux} />
            </section>
          )}
        </>
      )}
    </div>
  )
}
